/* eslint-disable react/require-default-props */
import { Box, IconButton, Spinner, SystemStyleObject, useDisclosure } from '@chakra-ui/core';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdBlurOn } from 'react-icons/md';
import { useStoreState } from '../../../models/hooks';
import { InfoHint } from '../../common/Tooltip/Tooltip';
import { useVideoEffect } from './hooks/useVideoEffect';
import { LocalStateSync } from './LocalStateSync';
import { VideoEffectStore } from './store/videoEffectModel';
import { VideoEffectsPanel } from './VideoEffectsPanel';

interface VideoEffectsProps {
  isLoading: boolean;
  videoContainerRef: React.RefObject<HTMLElement>;
  rightPanelRef?: React.RefObject<HTMLElement>;
  buttonStyle?: SystemStyleObject;
  marginLeft?: number;
  iconColor?: string;
}

function VideoEffectComponent({
  isLoading,
  videoContainerRef,
  rightPanelRef,
  buttonStyle,
  marginLeft = 4,
  iconColor = 'white',
}: VideoEffectsProps) {
  const { t } = useTranslation('videoInterviewEffect');
  const localVideoTrack = VideoEffectStore.useStoreState((actions) => actions.localVideoTrack);

  const isVideoFrameSupported = VideoEffectStore.useStoreState((actions) => actions.isVideoFrameSupported);
  const isFetchingPreferences = VideoEffectStore.useStoreState((actions) => actions.isFetchingPreferences);

  const { applyBlur, applyBackground, removeAllEffects } = useVideoEffect('/twilio-assets');

  const { isOpen: isEffectsOpen, onOpen: onEffectsOpen, onClose: onEffectsClose } = useDisclosure();

  const isBackgroundEffectDisabled = useMemo(
    () => isLoading || !localVideoTrack || !isVideoFrameSupported || isFetchingPreferences,
    [isLoading, localVideoTrack, isVideoFrameSupported, isFetchingPreferences],
  );

  const buttonIcon = isFetchingPreferences ? (
    <Spinner size="sm" color={iconColor} />
  ) : (
    <MdBlurOn color={isEffectsOpen ? 'white' : iconColor} size={24} />
  );

  function onEffectsClick() {
    // istanbul ignore next
    if (isBackgroundEffectDisabled) return;
    if (isEffectsOpen) {
      onEffectsClose();
    } else {
      onEffectsOpen();
    }
  }

  const buttonSx = useMemo<SystemStyleObject>(
    () => ({
      ...buttonStyle,
      ...(isEffectsOpen && { backgroundColor: 'blue.500' }),
    }),
    [buttonStyle, isEffectsOpen],
  );

  return (
    <>
      <Box ml={marginLeft} data-testid="video-effects-button">
        <InfoHint label={t('buttonTooltip')} width="fit-content" placement="left">
          <IconButton
            onClick={onEffectsClick}
            isDisabled={isBackgroundEffectDisabled}
            isLoading={isFetchingPreferences}
            title={isVideoFrameSupported ? t('buttonTooltip') : t('errors.videoNotSupported')}
            aria-label="Background Effects"
            className={`video-effects-button ${isEffectsOpen ? 'active' : ''}`}
            icon={buttonIcon}
            sx={buttonSx}
            colorScheme={isEffectsOpen ? 'blue' : undefined}
            isRound
          />
        </InfoHint>
      </Box>

      <VideoEffectsPanel
        isOpen={isEffectsOpen}
        onClose={onEffectsClose}
        applyBlur={applyBlur}
        applyBackground={applyBackground}
        removeAllEffects={removeAllEffects}
        videoContainerRef={videoContainerRef}
        rightPanelRef={rightPanelRef}
      />
    </>
  );
}

export default function VideoEffects(props: VideoEffectsProps) {
  const httpClient = useStoreState((state) => state.http.client);

  return (
    <VideoEffectStore.Provider initialData={{ httpClient }}>
      <LocalStateSync />
      <VideoEffectComponent {...props} />
    </VideoEffectStore.Provider>
  );
}
